/* Optional, off-by-default: scores a pass against the browser's built-in
   speech recognition instead of (or alongside) mic RMS/voice-activity.
   Chrome-family only (webkitSpeechRecognition); everywhere else this is
   simply unsupported and the caller falls back to self-judge or mic-RMS. */

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;
type SpeechRecognitionLike = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start: () => void;
  stop: () => void;
  onresult: ((e: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
};

function getCtor(): SpeechRecognitionCtor | null {
  const w = window as unknown as { webkitSpeechRecognition?: SpeechRecognitionCtor; SpeechRecognition?: SpeechRecognitionCtor };
  return w.webkitSpeechRecognition ?? w.SpeechRecognition ?? null;
}

export function isSpeechRecognitionSupported(): boolean {
  return typeof window !== "undefined" && getCtor() !== null;
}

export type SpeechSession = { stop: () => void; getTranscript: () => string };

/** Starts a continuous recognizer that keeps appending finalized phrases —
    call stop() when the drill's reps are done and read getTranscript(). */
export function startContinuousRecognition(lang: "en" | "sv"): SpeechSession | null {
  const Ctor = getCtor();
  if (!Ctor) return null;

  const rec = new Ctor();
  rec.lang = lang === "sv" ? "sv-SE" : "en-US";
  rec.continuous = true;
  rec.interimResults = false;
  rec.maxAlternatives = 1;

  let transcript = "";
  rec.onresult = (e) => {
    const parts: string[] = [];
    for (let i = 0; i < e.results.length; i++) parts.push(e.results[i]![0]!.transcript);
    transcript = parts.join(" ");
  };
  rec.onerror = () => {
    /* denied, no-speech, network — caller just gets whatever transcript exists so far */
  };
  rec.onend = () => {
    /* some browsers end a continuous session after a silence gap; nothing to do here */
  };

  try {
    rec.start();
  } catch {
    return null;
  }

  return {
    stop: () => {
      try {
        rec.stop();
      } catch {
        /* already stopped */
      }
    },
    getTranscript: () => transcript,
  };
}

function normalize(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, "")
    .trim();
}

export type TranscriptScore = { accuracy: number; droppedWords: string[] };

/** Token-overlap scoring — not phoneme-precise, but honest about what it is:
    "did the words you said match the words on the card." */
export function scoreTranscript(target: string, heard: string): TranscriptScore {
  const targetWords = normalize(target).split(/\s+/).filter(Boolean);
  const heardWords = new Set(normalize(heard).split(/\s+/).filter(Boolean));
  if (targetWords.length === 0) return { accuracy: 0, droppedWords: [] };
  const dropped = targetWords.filter((w) => !heardWords.has(w));
  const accuracy = Math.round(((targetWords.length - dropped.length) / targetWords.length) * 100);
  return { accuracy, droppedWords: dropped };
}
